import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../services/api';
import { Badge, qualityStatusVariant } from '../shared/Badge';
import EmptyState from '../shared/EmptyState';
import './ProjectQualityTable.css';

interface QualityRow {
  name: string;
  quality_status: string;
  pass_rate: number;
  coverage_percentage: number;
  failed_count: number;
  trend: 'up' | 'down' | 'flat';
}

interface EpicRow extends QualityRow {
  story_count: number;
}

const TrendIcon: React.FC<{ trend: string }> = ({ trend }) => {
  if (trend === 'up') return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3"><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
  if (trend === 'down') return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3"><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9099a8" strokeWidth="3"><path d="M5 12h14"/></svg>;
};

const QualityCells: React.FC<{ row: QualityRow }> = ({ row }) => (
  <>
    <td><Badge variant={qualityStatusVariant(row.quality_status)} dot>{row.quality_status}</Badge></td>
    <td>{row.pass_rate}%</td>
    <td>{row.coverage_percentage}%</td>
    <td>{row.failed_count}</td>
    <td><TrendIcon trend={row.trend} /></td>
  </>
);

interface Props {
  limit?: number;
}

export const ProjectQualityTable: React.FC<Props> = ({ limit = 8 }) => {
  const [loading, setLoading] = useState(true);
  const [epics, setEpics] = useState<EpicRow[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [storiesByEpic, setStoriesByEpic] = useState<Record<string, QualityRow[] | 'loading' | 'error'>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(`/api/v1/dashboard/epic-quality?limit=${limit}`)
      .then(resp => { if (!cancelled) setEpics((resp.data as any).epics || []); })
      .catch(() => { if (!cancelled) setEpics([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  const toggleExpand = (epicId: string) => {
    if (expanded === epicId) {
      setExpanded(null);
      return;
    }
    setExpanded(epicId);
    if (storiesByEpic[epicId]) return; // already fetched
    setStoriesByEpic(prev => ({ ...prev, [epicId]: 'loading' }));
    apiClient.get(`/api/v1/dashboard/epic-quality/stories?epic_id=${encodeURIComponent(epicId)}&limit=15`)
      .then(resp => {
        setStoriesByEpic(prev => ({ ...prev, [epicId]: (resp.data as any).stories || [] }));
      })
      .catch(() => {
        setStoriesByEpic(prev => ({ ...prev, [epicId]: 'error' }));
      });
  };

  return (
    <div className="pqt-card">
      <div className="pqt-header">
        <h3>Epic Quality Overview</h3>
        <Link to="/test-scenarios" className="pqt-view-all">Search by Epic →</Link>
      </div>
      {loading ? (
        <div className="pqt-skeleton" />
      ) : epics.length === 0 ? (
        <EmptyState message="No Epic IDs tagged yet." />
      ) : (
        <div className="pqt-table-wrap">
          <table className="pqt-table">
            <thead>
              <tr>
                <th title="Click a row to see its story/ticket-level folders. Most recently tagged epic shown first.">Epic ID</th>
                <th title="Healthy / Attention / At Risk / Critical — same rule as the Dashboard's overall Quality Health.">Quality</th>
                <th title="% of executions that passed, all-time within this epic.">Pass %</th>
                <th title="% of scenarios executed at least once, ever.">Coverage</th>
                <th title="Total FAIL executions, all-time.">Failed</th>
                <th title="Pass rate over the last 30 days vs. the 30 days before that.">Trend</th>
              </tr>
            </thead>
            <tbody>
              {epics.map(e => {
                const isOpen = expanded === e.name;
                const stories = storiesByEpic[e.name];
                return (
                  <React.Fragment key={e.name}>
                    <tr className="pqt-row-project" onClick={() => toggleExpand(e.name)}>
                      <td className="pqt-name">
                        <svg
                          className={`pqt-chevron${isOpen ? ' pqt-chevron-open' : ''}`}
                          width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"
                        >
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                        {e.name}
                        <span className="pqt-story-count">{e.story_count} {e.story_count === 1 ? 'story' : 'stories'}</span>
                      </td>
                      <QualityCells row={e} />
                    </tr>
                    {isOpen && (
                      stories === 'loading' ? (
                        <tr className="pqt-row-story"><td colSpan={6} className="pqt-story-status">Loading stories…</td></tr>
                      ) : stories === 'error' ? (
                        <tr className="pqt-row-story"><td colSpan={6} className="pqt-story-status">Failed to load stories.</td></tr>
                      ) : !stories || stories.length === 0 ? (
                        <tr className="pqt-row-story"><td colSpan={6} className="pqt-story-status">No stories tagged under "{e.name}".</td></tr>
                      ) : (
                        stories.map(s => (
                          <tr key={`${e.name}-${s.name}`} className="pqt-row-story">
                            <td className="pqt-story-name">{s.name}</td>
                            <QualityCells row={s} />
                          </tr>
                        ))
                      )
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectQualityTable;
